// Fire-and-forget client-side error reporter. Invariants: never throws,
// never awaits in user paths, never sends identity (server fills from
// session cookie), self-throttles to avoid render-loop DDoS.

import type {
  ClientErrorReport,
  ClientErrorAck,
  ClientErrorKind,
} from "@/lib/client-error-types";
import { getSessionId } from "@/lib/session-id";

const ENDPOINT = "/api/client-errors";

const RATE_LIMIT_PER_MIN = 20;
const DEDUP_WINDOW_MS = 5_000;
const DEDUP_MAX_ENTRIES = 200;
const MESSAGE_MAX = 500;
const STACK_MAX = 8_192;
const CONTEXT_JSON_MAX = 16_384;

const SCREENSHOT_PER_FINGERPRINT_WINDOW_MS = 10 * 60_000;
const SCREENSHOT_SESSION_CAP = 3;
const SCREENSHOT_SKIP_KINDS: ReadonlySet<ClientErrorKind> = new Set<ClientErrorKind>([
  "validation",
  "permission",
]);

// Kinds that pass the server-side notify filter (mirrors NOTIFY_KINDS in
// client-error-notify.ts, which is server-only). Used to gate sourcemap
// resolution so we don't pay 0.2–1.5 s per validation/permission report
// whose stack will never reach an email.
const EMAIL_ELIGIBLE_KINDS: ReadonlySet<ClientErrorKind> = new Set<ClientErrorKind>([
  "js_exception",
  "network",
  "auth",
  "api_error",
]);

const SECRET_KEYS = /^(password|token|authorization|apikey|api_key|secret|cookie)$/i;

const recent = new Map<string, number>();
let count = 0;
let windowStart = Date.now();

const screenshotsByFingerprint = new Map<string, number>();
let screenshotsThisSession = 0;

// Drop expired entries; if the map is still over the soft threshold, drop
// oldest insertion-order entries until it isn't. Without the second pass,
// a tab that fires many distinct fingerprints inside the dedup window
// would grow unboundedly.
function pruneRecent(now: number): void {
  for (const [k, t] of recent) {
    if (now - t > DEDUP_WINDOW_MS) recent.delete(k);
  }
  if (recent.size <= DEDUP_MAX_ENTRIES) return;
  const overflow = recent.size - DEDUP_MAX_ENTRIES;
  const it = recent.keys();
  for (let i = 0; i < overflow; i++) {
    const next = it.next();
    if (next.done) break;
    recent.delete(next.value);
  }
}

// Next.js uses thrown errors with `digest` starting "NEXT_REDIRECT" or
// "NEXT_NOT_FOUND" as control flow from server actions and `redirect()` /
// `notFound()` calls. They bubble through ErrorBoundary and the global
// handlers exactly like real exceptions but they are not errors — filter them
// out at every capture site before we report.
export function isNextInternalThrow(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { message?: unknown; digest?: unknown };
  const message = typeof e.message === "string" ? e.message : "";
  const digest = typeof e.digest === "string" ? e.digest : "";
  return (
    message === "NEXT_REDIRECT" ||
    message === "NEXT_NOT_FOUND" ||
    digest.startsWith("NEXT_REDIRECT") ||
    digest.startsWith("NEXT_NOT_FOUND")
  );
}

// Synthesize a call-site stack when the caller didn't pass one (e.g. a
// `!res.ok` report has no thrown error to forward). The first three frames
// are stack-internal and reportError's own — drop them so the slice starts
// at the actual caller.
function captureStack(): string | undefined {
  const raw = new Error().stack;
  if (!raw) return undefined;
  return raw.split("\n").slice(3).join("\n");
}

function stripSecrets(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripSecrets);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = SECRET_KEYS.test(k) ? "[redacted]" : stripSecrets(v);
    }
    return out;
  }
  return value;
}

function shouldCaptureScreenshot(
  kind: ClientErrorKind,
  fingerprint: string,
  now: number,
): boolean {
  if (SCREENSHOT_SKIP_KINDS.has(kind)) return false;
  if (screenshotsThisSession >= SCREENSHOT_SESSION_CAP) return false;
  const last = screenshotsByFingerprint.get(fingerprint);
  if (last && now - last < SCREENSHOT_PER_FINGERPRINT_WINDOW_MS) return false;
  return true;
}

// Two RAFs so any pending React render (caller's `setError(...)` after a
// failed apiFetch, ErrorBoundary's fallback) has committed before snapdom
// rasterizes — otherwise the JPEG shows the pre-error UI.
function waitForNextRender(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== "function") {
      setTimeout(resolve, 32);
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

// Returns true iff a JPEG was actually produced. The caller stamps the
// 10-min per-fingerprint slot only on `true` so a snapdom failure or
// 0-rect node leaves the next identical error retryable.
// Always invoked, regardless of whether capture/PUT succeeded — the
// /notify endpoint fetches whatever's in GCS (might be nothing) and
// emails either way. If the client crashes before reaching this call,
// the server's 60s fallback timer (route.ts) catches it.
function triggerNotify(id: string): void {
  void fetch(`/api/client-errors/${encodeURIComponent(id)}/notify`, {
    method: "POST",
    keepalive: true,
    credentials: "same-origin",
  }).catch(() => {});
}

async function uploadScreenshot(node: HTMLElement, ack: ClientErrorAck): Promise<boolean> {
  try {
    await waitForNextRender();
    const { captureNodeAsJpeg } = await import("@/lib/screenshot-capture");
    const blob = await captureNodeAsJpeg(node);
    if (!blob) {
      triggerNotify(ack.id);
      return false;
    }
    // No `keepalive: true` — fetch caps keepalive bodies at 64 KB and
    // JPEGs run 50–200 KB. If the tab unloads mid-PUT we lose the image
    // but the row still writes (the report itself uses keepalive).
    await fetch(ack.upload_url, {
      method: "PUT",
      headers: { "Content-Type": "image/jpeg" },
      body: blob,
    }).catch(() => {});
    triggerNotify(ack.id);
    return true;
  } catch {
    triggerNotify(ack.id);
    return false;
  }
}

export function reportError(
  input: Partial<ClientErrorReport> & Pick<ClientErrorReport, "kind" | "message">,
  captureNode?: HTMLElement | null,
): void {
  try {
    const now = Date.now();
    if (now - windowStart > 60_000) {
      count = 0;
      windowStart = now;
    }
    if (++count > RATE_LIMIT_PER_MIN) return;

    const route =
      input.route ?? (typeof location !== "undefined" ? location.pathname : undefined);

    const fingerprint = `${input.kind}|${input.message}|${route ?? ""}`;
    const last = recent.get(fingerprint);
    if (last && now - last < DEDUP_WINDOW_MS) return;
    recent.set(fingerprint, now);

    if (recent.size > DEDUP_MAX_ENTRIES) pruneRecent(now);

    let context = input.context ? (stripSecrets(input.context) as Record<string, unknown>) : undefined;
    if (context) {
      const json = JSON.stringify(context);
      if (json.length > CONTEXT_JSON_MAX) {
        context = { _truncated: true, preview: json.slice(0, CONTEXT_JSON_MAX) };
      }
    }

    // Capture-node resolution: a caller that doesn't pass anything gets
    // a full-viewport body capture by default. Callers that explicitly
    // pass `null` (e.g. apiFetch on a 4xx) opt out — we preserve that
    // distinction by checking against `undefined`, not nullish.
    const captured: HTMLElement | null =
      captureNode === undefined
        ? typeof document !== "undefined" ? document.body : null
        : captureNode;

    const wantsScreenshot =
      !!captured && shouldCaptureScreenshot(input.kind, fingerprint, now);

    const rawStack = input.stack ?? captureStack();

    // Async tail: resolve sourcemaps then POST. The sync gates above
    // (rate limit + 5-sec dedup) already throttle this layer, so the
    // async work below isn't called more than once per fingerprint per
    // window. `keepalive: true` lets the POST survive unload AND lets
    // the screenshot branch read the upload URL out of the response.
    //
    // Stack resolution is gated to email-eligible kinds — the resolver
    // pulls down sourcemaps via stacktrace-gps (~0.2–1.5 s), which is
    // wasted work on `validation`/`permission`/`other` kinds that the
    // notify filter rejects anyway.
    void (async () => {
      try {
        let stack: string | undefined;
        if (rawStack) {
          if (EMAIL_ELIGIBLE_KINDS.has(input.kind)) {
            try {
              const resolver = await import("@/lib/stack-resolver");
              stack = (await resolver.resolveStack(rawStack)).slice(0, STACK_MAX);
            } catch {
              stack = rawStack.slice(0, STACK_MAX);
            }
          } else {
            stack = rawStack.slice(0, STACK_MAX);
          }
        }

        const payload: ClientErrorReport = {
          occurred_at: input.occurred_at ?? new Date().toISOString(),
          severity: input.severity ?? "error",
          kind: input.kind,
          message: input.message.slice(0, MESSAGE_MAX),
          error_code: input.error_code,
          route,
          resource_type: input.resource_type,
          resource_id: input.resource_id,
          request_id: input.request_id,
          session_id: getSessionId() ?? undefined,
          stack,
          context,
          wants_screenshot: wantsScreenshot || undefined,
        };

        const res = await fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
          credentials: "same-origin",
        });
        if (!wantsScreenshot || !captured) return;
        if (res.status !== 200) return;
        let ack: ClientErrorAck | null = null;
        try {
          ack = (await res.json()) as ClientErrorAck;
        } catch {
          return;
        }
        if (!ack?.upload_url) return;
        // Stamp the per-fingerprint slot only after a real capture — the
        // 5-sec `recent` map already throttles burst loops at this layer.
        if (await uploadScreenshot(captured, ack)) {
          screenshotsByFingerprint.set(fingerprint, now);
          screenshotsThisSession += 1;
        }
      } catch {
        // an error reporter must never throw
      }
    })();
  } catch {
    // an error reporter must never throw
  }
}
